<template>
<transition name="slide">
  <el-dialog
    :class="slidingPopupClass"
    :style="{maxWidth: maxWidth}"
    :modal-class="slidingModalClass"
    :title="title"
    :visible.sync="visible"
    :close-on-click-modal="closeOnClickModal"
    :modal-append-to-body="popupModalAppendToBody"
    :append-to-body="popupAppendToBody"
    :modal="true"
    top="0"
    :width="popupWidth"
    @open="open"
    @close="close"
    >
    <!-- title -->
    <el-row slot="title" type="flex" justify="space-between">
      <span class="el-dialog__title">{{ title }}</span>
      <div style="margin-right:30px;">
        <slot name="title"/>
      </div>
    </el-row>
    <!-- body -->
    <el-scrollbar v-if="defaultContent === true" class="bar-visible">
      <div class="body-content">
        <slot name="content" />
      </div>
    </el-scrollbar>
    <slot v-else name="content" /> <!-- NOTE: 만약 스크롤 없는 커스텀 화면을 잡는 경우 클래스를 'body-content-no-scroll'로 준다 -->
    <!-- footer -->
    <div slot="footer">
      <slot name="footer" />
    </div>
  </el-dialog>
</transition>
</template>

<script>
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { setVModalClassBySlidingType } from '@/utils/popup';

const popupType = {
  COMMON: 'common',
  SETTING: 'setting',
  FULL: 'full',
};

export default {
  name: 'WSlidingPopup',
  props: {
    /**
     * 슬라이딩 팝업 타입
     * 1. 'common' (default): 일반 슬라이딩 팝업.
     * 2. 'setting': 설정용 슬라이딩 팝업
     * 3. 'full': 프레임 영역을 제외한 컨텐츠 영역 슬라이딩 팝업
     */
    type: {
      type: String,
      default: popupType.COMMON,
    },
    /**
     * 팝업 타이틀
     */
    title: {
      type: String,
    },
    /**
     * 팝업 너비
     */
    width: {
      type: String,
    },
    /**
     * 팝업 커스텀 클래스
     */
    popupClass: {
      type: String,
    },
    /**
     * 모달 커스텀 클래스
     */
    modalClass: {
      type: String,
    },
    /**
     * 모달 클릭 시 팝업 닫기 여부
     */
    closeOnClickModal: {
      type: Boolean,
      default: false,
    },
    /**
     * 모달 Body 영역 추가 여부
     */
    modalAppendToBody: {
      type: Boolean,
      default: true,
    },
    /**
     * 팝업 Body 영역 추가 여부
     */
    appendToBody: {
      type: Boolean,
      default: true,
    },
    /**
     * 모달 제공 여부
     */
    modal: {
      type: Boolean,
      default: true,
    },
    defaultContent: {
      type: Boolean,
      default: true,
    },
    /**
     * 프레임에서 관리되는 팝업인지 여부
     */
    isFramePopup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * 팝업 display 여부
       * @type {Boolean}
       */
      visible: false,
    };
  },
  computed: {
    /**
     * body 영역 높이 설정 클래스
     * 1. footer가 포함되는 경우 (include-footer)
     * 2. footer가 포함되지 않는 경우 (no-footer)
     * @author Ju
     * @return {String} 클래스명
     */
    bodyHeightClass() {
      if (isNil(this.$slots.footer)) {
        return 'no-footer';
      }
      return 'include-footer';
    },
    /**
     * fullscreen layer display 여부
     * @author Ju
     * @return {Boolean}
     */
    showFullscreenLayer() {
      return this.$store.state.common.showFullscreenLayer;
    },
    /**
     * 설정 팝업 display 여부
     * @author Ju
     * @return {[type]} [description]
     */
    showSettingView() {
      return this.$store.state.common.showSettingView;
    },
    /**
     * 슬라이딩 팝업 클래스
     * @author Ju
     * @return {Array} 클래스 배열
     */
    slidingPopupClass() {
      const classes = ['sliding', this.bodyHeightClass, this.popupClass];
      if (this.type === popupType.SETTING) {
        classes.unshift('setting-sliding');
      } else if (this.type === popupType.FULL) {
        classes.unshift('full-sliding');
      }

      if (this.showFullscreenLayer && !this.showSettingView) {
        classes.unshift('is-fullscreen');
      }

      return classes;
    },
    /**
     * 슬라이딩 팝업 모달 클래스
     * @author Ju
     * @return {String} 모달 클래스명
     */
    slidingModalClass() {
      let modalClassName = null;
      if (isEmpty(this.modalClass)) {
        if (this.showFullscreenLayer && !this.showSettingView) {
          modalClassName = 'is-fullscreen';
        } else if (this.type === popupType.SETTING) {
          modalClassName = 'setting-sliding';
        } else {
          modalClassName = 'sliding';
        }
      } else {
        modalClassName = this.modalClass;
      }
      return modalClassName;
    },
    /**
     * 팝업 Body 영역 추가 여부
     * @author Ju
     * @return {Boolean}
     */
    popupAppendToBody() {
      return this.type === popupType.SETTING ? true : this.appendToBody;
    },
    /**
     * 팝업 모달 Body 영역 추가 여부
     * @author Ju
     * @return {Boolean}
     */
    popupModalAppendToBody() {
      return this.type === popupType.SETTING ? true : this.modalAppendToBody;
    },
    /**
     * 팝업 너비
     * @author Ju
     * @return {String}
     */
    popupWidth() {
      if (this.type === popupType.FULL) {
        return '';
      } else if (isEmpty(this.width)) {
        return '50%';
      }
      return this.width;
    },
    /**
     * 팝업 최대 너비
     * Full Sliding Popup을 제외한 팝업 최대 크기 800px로 제한.
     * @author Ju
     * @return {[type]} [description]
     */
    maxWidth() {
      return this.type === popupType.FULL ? null : '800px';
    },
  },
  watch: {
    /**
     * showFullscreenLayer watch
     * @author Ju
     * @param  {Boolean} to showFullscreenLayer 현재 값
     */
    showFullscreenLayer(to) {
      // 설정 슬라이딩 팝업일 경우 환경설정 팝업이 닫히면 같이 닫히도록 한다.
      if (this.type === popupType.SETTING && to === false && this.visible === true) {
        this.close();
      }
    },
  },
  methods: {
    /**
     * 슬라이딩 팝업 열기
     * @author Ju
     */
    open() {
      this.visible = true;
      if (this.isFramePopup) {
        setVModalClassBySlidingType();
      }
      this.$emit('open');
    },
    /**
     * 슬라이딩 팝업 닫기
     * @author Ju
     */
    close() {
      this.visible = false;
      this.$emit('close');
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/popup/SlidingPopup/SlidingPopup.vue